import { Subscription } from 'rxjs';
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { ObjectTypes } from "@common/enums/object-types";
import { GroupValueHelpers, GroupKeyComparatorHelpers, RecordComparatorHelpers, ResultGroup2, ResultGrouping2Definition } from "@common/data-models/search-results/result-group";
import { SearchResults } from "@common/data-models/search-results/search-results";
import { DataStore } from '@common/data-models/data-store/data-store';
import { Record } from "@common/data-models/records/record";
import { PublicationRecord } from "@cast/data-models/records/publication-record";
import { PublicationListRecord } from "@cast/data-models/records/publication-list-record";
import { PublicationChannelRecord } from '@common/data-models/records/publication-channel-record';
import { NewlyCreatedRecords } from "@suite/common/data-models/newly-created-records/newly-created-records";
import { ICastService } from "@suite/cast/interfaces/i-cast-service";
import { map, takeUntil } from 'rxjs/operators';

export class CastSearchResults extends SearchResults {

    public static castService: ICastService = null;

    constructor() {
        super();
        CastSearchResults.newlyAddedItems = new NewlyCreatedRecords();
    }
    public publicationListChanged: () => void = null;

    private _publicationListSubscription: Subscription = null;
    private _publicationListChildrenSubscription: Subscription = null;
    private _publicationList: PublicationListRecord = null;
    public get publicationList(): PublicationListRecord {
        return this._publicationList;
    }
    public set publicationList(v: PublicationListRecord) {
        this._publicationList = v;
        this.disposeSubscriptions();
        if (this._publicationList) {
            this._publicationListSubscription = this._publicationList.changeObserver.subscribe(() => {
                if (this.publicationListChanged) this.publicationListChanged();
            });
            this._publicationListChildrenSubscription = this._publicationList.collectionChangeObserver.subscribe(() => {
                if (this.publicationListChanged) this.publicationListChanged();
            });
        }
    }

    public publicationListsOfRestrictedChannelTypes: Array<PublicationListRecord> = [];

    public disposeSubscriptions() {
        if (this._publicationListSubscription) this._publicationListSubscription.unsubscribe();
        if (this._publicationListChildrenSubscription) this._publicationListChildrenSubscription.unsubscribe();
    }

    public channel: PublicationChannelRecord = null;

    public addPublication(publication: PublicationRecord) {
        if (this.publicationList) {
            this.publicationList.addChildren(publication);
            this.byGuid[publication.guid] = publication;
        }
    }

    public getAllResults(): Array<PublicationRecord> {
        let ret: Array<PublicationRecord> = [];
        if (this.publicationList != null) {
            for (let record of this.publicationList.getChildren()) {
                ret.push(<PublicationRecord>record);
            }
        }
        return ret;
    }

    public publicationGroups2: { [id: string]: ResultGroup2<PublicationRecord> } = {};

    private static _publicationGroup2Defs: { [id: string]: ResultGrouping2Definition } = {};
    public static get publicationGroup2Defs(): { [id: string]: ResultGrouping2Definition } {
        if ((Object.keys(CastSearchResults._publicationGroup2Defs).length == 0)) {

            CastSearchResults._publicationGroup2Defs[PSTFieldNames.PSTChannel] = new ResultGrouping2Definition(
                GroupValueHelpers.StringOrDefault(PSTFieldNames.PSTChannel, "No Value"),
                GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No Value"),
                GroupValueHelpers.Time(PSTFieldNames.PSTPublicationTime),
                GroupKeyComparatorHelpers.DisplayValue(),
                RecordComparatorHelpers.DateTime(PSTFieldNames.PSTPublicationTime)
            );
            CastSearchResults._publicationGroup2Defs[PSTFieldNames.PSTTopicFileDate] = new ResultGrouping2Definition(
                GroupValueHelpers.StringOrDefault(PSTFieldNames.PSTTopicFileDate, "No Value"),
                GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No Value"),
                GroupValueHelpers.Time(PSTFieldNames.PSTPublicationTime),
                GroupKeyComparatorHelpers.DisplayValue(),
                RecordComparatorHelpers.DateTime(PSTFieldNames.PSTPublicationTime)
            );
        }
        return CastSearchResults._publicationGroup2Defs;
    }

    private get publicationGroupFields(): Array<string> {
        return CastSearchResults.castService.groupByPublicationKeys;
    }

    public remove(rec: Record) {
        super.remove(rec);
        if (this.publicationList) this.publicationList.removeChildByGuid(rec.guid);
        for (let field in CastSearchResults.publicationGroup2Defs) {
            if (this.publicationGroups2[field]) {
                this.publicationGroups2[field].remove(CastSearchResults.publicationGroup2Defs[field].valueConvertor(rec), <PublicationRecord>rec);
            }
        }
    }

    public sortGroups(sortBy: any, sortOrder: string, groupSortOrder: string) {
        let groups = this.getGroups();

        for (let field of groups) {
            this.publicationGroups2[field].sort(CastSearchResults.publicationGroup2Defs[field].groupKeyComparator(groupSortOrder), CastSearchResults.publicationGroup2Defs[sortBy].groupKeyComparator2(sortOrder), CastSearchResults.publicationGroup2Defs[sortBy].recordComparator(sortOrder));
        }
    }

    public dispose() {
        super.dispose();
        this.disposeSubscriptions();
        this.objectType = null;
        this.publicationList = null;
        this.users = [];
        this.publicationGroups2 = {};
    }

    public getGroups(): Array<string> {
        switch (this.objectType) {
            case ObjectTypes.PUBLICATION:
                return ["PSTTargetDate"];
            default:
                return ["Type"];
        }
    }

    //TODO
    /*public insertAfterPublication(publicationToInsert: PublicationRecord) {
        this.publications.push(publicationToInsert);
        this.byGuid[publicationToInsert.guid] = publicationToInsert;
        let groups = this.getGroups();
        for (let field of groups) {
            this.publicationGroups2[field].insertAfterEntry(CastSearchResults.publicationGroup2Defs[field].valueConvertor(publicationToInsert), publicationToInsert);
        }
    }*/

    public rebuildGroups(groupBy: string, sortBy: any, sortOrder: string, groupSortOrder: string) {
        /*
        let groups = this.getGroups();

        // Clear all groups
        for (let field of groups) {
            if (!this.publicationGroups2[field]) this.publicationGroups2[field] = new ResultGroup2<PublicationRecord>();
            this.publicationGroups2[field].clear();
        }
        if (!this.publicationGroups2[SearchResults.groupByField]) {
            SearchResults.groupByField = groupBy;
        }

        // Build active group
        if (this.publicationList != null) {
            for (let pub of this.publicationList.getChildren()) {
                this.publicationGroups2[SearchResults.groupByField].addToGroups(CastSearchResults.publicationGroup2Defs[SearchResults.groupByField].valueConvertor(pub), CastSearchResults.publicationGroup2Defs[SearchResults.groupByField].valueConvertor(pub), <PublicationRecord>pub);
            }
            //don't sort. order is always manually defined
            // this.publicationGroups2[SearchResults.groupByField].sort(CastSearchResults.publicationGroup2Defs[SearchResults.groupByField].groupKeyComparator(groupSortOrder), CastSearchResults.publicationGroup2Defs[SearchResults.groupByField].groupKeyComparator2(groupSortOrder), CastSearchResults.publicationGroup2Defs[sortBy].recordComparator(sortOrder));
            if (this.publicationGroups2[SearchResults.groupByField]) {
                this.publicationGroups2[SearchResults.groupByField].isBuilt = true;
            }

            for (let field of groups) {
                if (!this.publicationGroups2[field]) return;
                if (this.publicationGroups2[field].isBuilt) {
                    continue;
                }

            for (let assignment of this.publications) {
                this.publicationGroups2[field].addToGroups(CastSearchResults.publicationGroup2Defs[field].valueConvertor(assignment), CastSearchResults.publicationGroup2Defs[field].valueConvertor2(assignment), assignment);
            }
            this.publicationGroups2[field].sort(CastSearchResults.publicationGroup2Defs[sortBy].groupKeyComparator(groupSortOrder), CastSearchResults.publicationGroup2Defs[sortBy].groupKeyComparator2(groupSortOrder), CastSearchResults.publicationGroup2Defs[sortBy].recordComparator(sortOrder));
            this.publicationGroups2[field].isBuilt = true;
        }*/
    }

    override buildTree(sections: any): void {
        let initialization: boolean = false;
        if (!this.treeControl || !this.treeControl.dataNodes || this.treeControl.dataNodes.length == 0) {
            initialization = true;
        }

        this.treeDataSource.disconnect();
        this.treeDataSource.connect(this).pipe(
            takeUntil(this.onDispose),
            map(data => {
                this.cardItemHeights = data.map(item => {
                    switch (item?.linkedRecordType) {
                        case ObjectTypes.ASSIGNMENT: {
                            switch (item.level) {
                                case 0: return 32;
                                case 1: return 32;
                                default: {
                                    if (item.nodeLastInGroup) {
                                        return 144;
                                    }
                                    return 136;
                                }
                            }
                        }
                        default: {
                            switch (item.level) {
                                case 0: return 32;
                                default: {
                                    if (item.nodeLastInGroup) {
                                        return 144;
                                    }
                                    return 136;
                                }
                            }
                        }
                    }
                });
                this.condensedItemHeights = data.map(item => {
                    switch (item?.linkedRecordType) {
                        default: {
                            switch (item.level) {
                                case 0: return 32;
                                default: {
                                    if (item.nodeLastInGroup) {
                                        return 56;
                                    } else if (item.nodeFirstInGroup) {
                                        return 72;
                                    }
                                    return 48;
                                }
                            }
                        }
                    }
                });
                this.cardItemHeightsCustomFields = data.map(item => {
                    switch (item?.linkedRecordType) {
                        case ObjectTypes.ASSIGNMENT: {
                            switch (item.level) {
                                case 0: return 32;
                                case 1: {
                                    if (item.isNewItems) {
                                        if (item.nodeLastInGroup) {
                                            return 144;
                                        }
                                        return 136;
                                    }
                                    return 32;
                                }
                                default: {
                                    if (item.nodeLastInGroup) {
                                        return 144;
                                    }
                                    return 136;
                                }
                            }
                        }
                        default: {
                            switch (item.level) {
                                case 0: return 32;
                                default: {
                                    if (item.nodeLastInGroup) {
                                        return 192;
                                    }
                                    return 184;
                                }
                            }
                        }
                    }
                });
            })
        ).subscribe();

        this.treeLikeData = sections.map((key:any, index: number) => {
            let publicationSequenceCollection: any = {};
            if (this.publicationList && this.publicationList.publicationSequenceCollection) {
                publicationSequenceCollection = JSON.parse(this.publicationList.publicationSequenceCollection);
            }
            return {
                id: key,
                label: key,
                itemsCount: publicationSequenceCollection[key]?.length,
                index: index,
                children: publicationSequenceCollection[key]?.map((entry, index) => {
                    let publicationRecord = DataStore.byGuid<PublicationRecord>(entry, null)
                    return {
                        id: publicationRecord?.guid,
                        children: [],
                        object: publicationRecord,
                        linkedRecordType: publicationRecord?.linkedRecord.type,
                        nodeFirstInGroup: index === 0,
                        nodeLastInGroup: (index === (publicationSequenceCollection[key].length - 1))
                    };
                })
            };
        });
        this.treeDataSource.data = this.treeLikeData;
        if (initialization) this.treeControl.expandAll();
    }
}
DataStore.registerSearchResults("cast", new CastSearchResults());
